import React, { useReducer, useState, useEffect } from "react";
import PropTypes from "prop-types";
import ShippingTable from "../../elements/Tables/ShippingTable/ShippingTable";
// import ShippingCreationForm from "../../elements/Forms/ShippingCreationForm/ShippingCreationForm";

import { useFetch } from "../../../Utils/useFetch";

import {
  FETCH_ALL_SHIPPING_API,
  FETCH_ALL_STOCK_API,
} from "../../../Constants/Apis";
import {
  Button,
  Flex,
  Input,
  message,
  Modal,
  DatePicker,
  Card,
  Statistic,
} from "antd";
import * as XLSX from "xlsx";
import { reducer } from "../../../Utils/CommonUtils";
import { deleteShipping } from "../../../Services/ShippingService";
import ShippingDashboardTable from "../../elements/Tables/ShippingDashboardTable/ShippingDashboardTable";
import dayjs from "dayjs";
const { Search } = Input;
const { RangePicker } = DatePicker;

const ShippingDashboardPage = ({ history, userData }) => {
  const initialState = {
    searchKeyword: "",
    filteredInfo: {},
    dateRange: {
      startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
    },
    sorter: {},
    current: 1,
    pageSize: 50,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shippingId, setShippingId] = useState(null);
  const [selectedNoSJ, setSelectedNoSJ] = useState(null);
  const [dateRange, setDateRange] = useState([]);

  const handleOpenModal = () => setIsModalVisible(true);
  const handleCloseModal = () => setIsModalVisible(false);

  const handleDelete = async (id) => {
    setShippingId(id);
    setIsModalOpen(true);
  };

  const handleCreate = async () => {
    history.push("/user/create-shipping");
  };

  const handleExport = () => {
    const dataArray = Array.isArray(stockData?.data) ? stockData.data : [];
    if (!dataArray.length) {
      message.warning("No data to export");
      return;
    }

    // Create header rows
    const headerRows = [
      {
        "Date Range":
          state.dateRange.startDate && state.dateRange.endDate
            ? `${state.dateRange.startDate} to ${state.dateRange.endDate}`
            : "All Dates",
      },
      {}, // Empty row
      {
        "Tanggal Kirim": "Tanggal Kirim",
        "Tanggal Terima ETA": "Tanggal Terima ETA",
        "Late by (days)": "Late by (days)",
        "No Bon": "No Bon",
        "No Surat Jalan": "No Surat Jalan",
        Status: "Status",
        "Kirim Ke": "Kirim Ke",
        Article: "Article",
        Color: "Color",
        Size: "Size",
        "Qty Kirim": "Qty Kirim",
        "Qty Terima": "Qty Terima",
        "Qty Difference": "Qty Difference",
      },
    ];

    const formattedData = dataArray.map((item) => ({
      "Tanggal Kirim": item.tanggal_kirim
        ? new Date(item.tanggal_kirim).toLocaleDateString("en-GB")
        : "N/A",
      "Tanggal Terima ETA": item.tanggal_terima_eta
        ? new Date(item.tanggal_terima_eta).toLocaleDateString("en-GB")
        : "N/A",
      "Late by (days)": item.days_until_eta,
      "No Bon": item.noForm,
      "No Surat Jalan": item.noSJ,
      Status: item.status,
      "Kirim Ke": item.counterCode,
      Article: item.article,
      Color: item.color,
      Size: item.size,
      "Qty Kirim": item.quantity,
      "Qty Terima": item.receivedQuantity,
      "Qty Difference": item.quantity_diff,
    }));

    // Combine header rows with data
    const allData = [...headerRows, ...formattedData];

    const worksheet = XLSX.utils.json_to_sheet(allData, { skipHeader: true });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Shipping Data");
    XLSX.writeFile(workbook, "Shipping_Data.xlsx");
  };

  const handleOk = async () => {
    try {
      await deleteShipping(shippingId);
      message.success("Shipping deleted successfully");
      callReFetchStock();
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Something went wrong, failed to delete shipping."
      );
    }
    setIsModalOpen(false);
  };

  // Build API URL with userCounterCode and date range parameters
  const buildApiUrl = () => {
    const baseUrl = `${FETCH_ALL_STOCK_API}/with-shipping`;
    const params = new URLSearchParams();

    // Add userCounterCode parameter if available
    if (userData?.counterCode) {
      params.append("userCounterCode", userData.counterCode);
    }

    // Add date range parameters if available
    if (state.dateRange?.startDate) {
      params.append("startDate", state.dateRange.startDate);
    }
    if (state.dateRange?.endDate) {
      params.append("endDate", state.dateRange.endDate);
    }

    // Add pagination parameters
    if (state.current) {
      params.append("current", state.current);
    }
    if (state.pageSize) {
      params.append("pageSize", state.pageSize);
    }

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  };

  const {
    fetchedData,
    callReFetch: callReFetchStock,
    loading: loadingStock,
  } = useFetch(buildApiUrl(), state);

  // Extract data and pagination from the response
  // Response structure: { success: true, data: { data: [...], pagination: {...} } }
  const stockData = fetchedData?.data?.data || { data: [], pagination: {} };

  useEffect(() => {
    if (selectedNoSJ) {
      callReFetchStock();
    }
  }, [selectedNoSJ]);

  const handleSearch = (value) => {
    dispatch({ type: "searchKeyword", payload: value });
    callReFetchStock();
  };

  const handleDateChange = (dates) => {
    const [startDate, endDate] = dates || [];
    setDateRange(dates);

    const formattedStartDate = startDate
      ? startDate.format("YYYY-MM-DD")
      : null;
    const formattedEndDate = endDate ? endDate.format("YYYY-MM-DD") : null;

    const range = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };

    dispatch({ type: "dateRange", payload: range });
    callReFetchStock();
  };

  // Calculate aggregated data
  // stockData is now { data: [...], pagination: {...} }
  const dataArray = Array.isArray(stockData?.data) ? stockData.data : [];
  const lateCount =
    dataArray.filter((item) => item.days_until_eta < 0).length || 0;
  const inDeliveryCount =
    dataArray.filter((item) => item.status === "in delivery").length || 0;
  const totalDifference =
    dataArray.reduce(
      (sum, item) =>
        item.status === "in stock" ? sum + (item.quantity_diff || 0) : sum,
      0
    ) || 0;

  return (
    <div
      style={{
        width: "100%",
        padding: "20px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowX: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "20px",
          width: "100%",
          maxWidth: "1200px",
          flexWrap: "wrap",
        }}
      >
        <Card title="Late Shipments" style={{ flex: 1, minWidth: "200px" }}>
          <Statistic value={lateCount} />
        </Card>
        <Card title="In Delivery" style={{ flex: 1, minWidth: "200px" }}>
          <Statistic value={inDeliveryCount} />
        </Card>
        <Card title="Total Difference" style={{ flex: 1, minWidth: "200px" }}>
          <Statistic value={totalDifference} />
        </Card>
      </div>
      <div
        style={{
          paddingBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1200px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Flex gap="small">
          <Button type="default" onClick={handleExport}>
            Export to Excel
          </Button>
          <RangePicker
            style={{ width: "100%" }}
            onChange={handleDateChange}
            defaultValue={[
              dayjs(state.dateRange.startDate),
              dayjs(state.dateRange.endDate),
            ]}
          />
          <Search
            placeholder="Search..."
            allowClear
            onSearch={handleSearch}
            style={{ width: "300px" }}
          />
        </Flex>
      </div>

      <ShippingDashboardTable
        history={history}
        loading={loadingStock}
        data={stockData}
        callReFetch={callReFetchStock}
        dispatch={dispatch}
      />

      {/* <Modal
        title="Create Shipping"
        open={isModalVisible}
        footer={null}
        onCancel={handleCloseModal}
      >
        <ShippingCreationForm
          history={history}
          userData={userData}
          callReFetch={callReFetch}
          onClose={handleCloseModal}
        />
      </Modal> */}

      <Modal
        title="Delete Shipping"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
      >
        <p>Are you sure you want to delete this shipping?</p>
      </Modal>
    </div>
  );
};

ShippingDashboardPage.propTypes = {};

export default ShippingDashboardPage;
